import { useState } from 'react';
import type { FlightFilters, FlightSortOption } from '../types/flight';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';

interface FlightFiltersProps {
  filters: FlightFilters;
  onFiltersChange: (filters: FlightFilters) => void;
  sortBy: FlightSortOption;
  onSortChange: (sort: FlightSortOption) => void;
  availableAirlines: string[];
  className?: string;
}

interface FilterSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function FilterSection({ title, isOpen, onToggle, children }: FilterSectionProps) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
      >
        {title}
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>
      {isOpen && <div className="pb-3">{children}</div>}
    </div>
  );
}

export default function FlightFilters({
  filters,
  onFiltersChange,
  sortBy,
  onSortChange,
  availableAirlines,
  className = '',
}: FlightFiltersProps) {
  const [expandedSections, setExpandedSections] = useState({
    stops: true,
    price: true,
    time: false,
    airlines: false,
    duration: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleStopsChange = (type: 'nonStop' | 'oneStop' | 'twoOrMoreStops') => {
    onFiltersChange({
      ...filters,
      stops: {
        ...filters.stops,
        [type]: !filters.stops[type as keyof typeof filters.stops],
      },
    });
  };

  const handlePriceChange = (type: 'min' | 'max', value: number) => {
    onFiltersChange({
      ...filters,
      priceRange: {
        ...filters.priceRange,
        [type]: value,
      },
    });
  };

  const handleTimeChange = (type: 'morning' | 'afternoon' | 'evening' | 'night') => {
    onFiltersChange({
      ...filters,
      departureTime: {
        ...filters.departureTime,
        [type]: !filters.departureTime[type as keyof typeof filters.departureTime],
      },
    });
  };

  const handleAirlineToggle = (airline: string) => {
    const newAirlines = filters.airlines.includes(airline)
      ? filters.airlines.filter(a => a !== airline)
      : [...filters.airlines, airline];
    
    onFiltersChange({
      ...filters,
      airlines: newAirlines,
    });
  };

  const handleDurationChange = (value: number) => {
    onFiltersChange({
      ...filters,
      duration: {
        ...filters.duration,
        maxHours: value,
      },
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      stops: { nonStop: true, oneStop: true, twoOrMoreStops: true },
      priceRange: { min: 0, max: 5000 },
      departureTime: { morning: true, afternoon: true, evening: true, night: true },
      airlines: [],
    });
  };

  const hasActiveFilters = 
    !filters.stops.nonStop || 
    !filters.stops.oneStop || 
    !filters.stops.twoOrMoreStops ||
    filters.priceRange.min > 0 ||
    filters.priceRange.max < 5000 ||
    !filters.departureTime.morning ||
    !filters.departureTime.afternoon ||
    !filters.departureTime.evening ||
    !filters.departureTime.night ||
    filters.airlines.length > 0;

  const activeFilterCount = 
    (!filters.stops.nonStop ? 1 : 0) +
    (!filters.stops.oneStop ? 1 : 0) +
    (!filters.stops.twoOrMoreStops ? 1 : 0) +
    (filters.priceRange.min > 0 ? 1 : 0) +
    (filters.priceRange.max < 5000 ? 1 : 0) +
    (!filters.departureTime.morning ? 1 : 0) +
    (!filters.departureTime.afternoon ? 1 : 0) +
    (!filters.departureTime.evening ? 1 : 0) +
    (!filters.departureTime.night ? 1 : 0) +
    filters.airlines.length;

  return (
    <div className={`bg-white rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-900">Filters</span>
          {hasActiveFilters && (
            <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Stops Filter */}
      <FilterSection
        title="Stops"
        isOpen={expandedSections.stops}
        onToggle={() => toggleSection('stops')}
      >
        <div className="space-y-2">
          {[
            { key: 'nonStop', label: 'Direct', count: 0 },
            { key: 'oneStop', label: '1 stop', count: 0 },
            { key: 'twoOrMoreStops', label: '2+ stops', count: 0 },
          ].map(option => (
            <label key={option.key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.stops[option.key as keyof typeof filters.stops] as boolean}
                onChange={() => handleStopsChange(option.key as 'nonStop' | 'oneStop' | 'twoOrMoreStops')}
                className="rounded text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Price Filter */}
      <FilterSection
        title={`Price: $${filters.priceRange.min} - $${filters.priceRange.max}`}
        isOpen={expandedSections.price}
        onToggle={() => toggleSection('price')}
      >
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Min: ${filters.priceRange.min}</span>
              <span>Max: ${filters.priceRange.max}</span>
            </div>
            <input
              type="range"
              min="0"
              max="5000"
              step="50"
              value={filters.priceRange.max}
              onChange={(e) => handlePriceChange('max', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
      </FilterSection>

      {/* Departure Time Filter */}
      <FilterSection
        title="Departure Time"
        isOpen={expandedSections.time}
        onToggle={() => toggleSection('time')}
      >
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: 'morning', label: 'Morning', icon: '🌅', timeRange: '6am - 12pm' },
            { key: 'afternoon', label: 'Afternoon', icon: '☀️', timeRange: '12pm - 6pm' },
            { key: 'evening', label: 'Evening', icon: '🌆', timeRange: '6pm - 12am' },
            { key: 'night', label: 'Night', icon: '🌙', timeRange: '12am - 6am' },
          ].map(option => (
            <label
              key={option.key}
              className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                filters.departureTime[option.key as keyof typeof filters.departureTime]
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="checkbox"
                checked={filters.departureTime[option.key as keyof typeof filters.departureTime]}
                onChange={() => handleTimeChange(option.key as 'morning' | 'afternoon' | 'evening' | 'night')}
                className="sr-only"
              />
              <span>{option.icon}</span>
              <div>
                <p className="text-sm font-medium text-gray-700">{option.label}</p>
                <p className="text-xs text-gray-500">{option.timeRange}</p>
              </div>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Airlines Filter */}
      {availableAirlines.length > 0 && (
        <FilterSection
          title={`Airlines (${filters.airlines.length} selected)`}
          isOpen={expandedSections.airlines}
          onToggle={() => toggleSection('airlines')}
        >
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {availableAirlines.map(airline => (
              <label key={airline} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.airlines.includes(airline)}
                  onChange={() => handleAirlineToggle(airline)}
                  className="rounded text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{airline}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Duration Filter */}
      <FilterSection
        title={`Max Duration: ${filters.duration.maxHours}h`}
        isOpen={expandedSections.duration}
        onToggle={() => toggleSection('duration')}
      >
        <div>
          <input
            type="range"
            min="1"
            max="24"
            step="1"
            value={filters.duration.maxHours}
            onChange={(e) => handleDurationChange(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>1h</span>
            <span>24h</span>
          </div>
        </div>
      </FilterSection>
    </div>
  );
}
